import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { submitRegistration, verifyOtp, resendOtp } from '@/api/academy-registration';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';

const THEME = ROLE_LOGIN_THEMES['academy_register'];

type Step = 'form' | 'otp' | 'status';

const STEP_LABELS: Record<Step, string> = {
  form: 'Step 1 of 3 · Your Details',
  otp: 'Step 2 of 3 · Verify Email',
  status: 'Step 3 of 3 · Submitted',
};

function StepIndicator({ step }: { step: Step }) {
  const steps: Step[] = ['form', 'otp', 'status'];
  const current = steps.indexOf(step);
  return (
    <div className="rl__stepper">
      <div className="rl__stepper-track">
        {steps.map((s, i) => (
          <span key={s} style={{ display: 'contents' }}>
            <div className={['rl__step-dot', i === current ? 'rl__step-dot--active' : '', i < current ? 'rl__step-dot--done' : ''].filter(Boolean).join(' ')} />
            {i < steps.length - 1 && <div className="rl__step-line" />}
          </span>
        ))}
      </div>
      <span className="rl__stepper-label">{STEP_LABELS[step]}</span>
    </div>
  );
}

export function AcademyRegisterPage() {
  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [academyName, setAcademyName] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');

  const [registrationId, setRegistrationId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      const result = await submitRegistration({
        display_name: displayName.trim(), email: email.trim(), password,
        academy_name: academyName.trim(), mobile: mobile.trim(), address: address.trim(),
      });
      setRegistrationId(result.id);
      setOtpMessage(result.message);
      if (result.auth) {
        localStorage.setItem('access_token', result.auth.accessToken || (result.auth as any).access_token);
        localStorage.setItem('refresh_token', result.auth.refreshToken || (result.auth as any).refresh_token);
      }
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length < 6) { setError('Please enter the complete 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const result = await verifyOtp(registrationId, otpCode);
      setStatusMessage(result.message);
      setStep('status');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    setResending(true); setError('');
    try {
      const result = await resendOtp(registrationId);
      setOtpMessage(result.message);
      setOtpCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code.');
    } finally { setResending(false); }
  };

  const meta = step === 'form' ? (
    <>
      <p className="rl__meta-row">Already have an account? <Link to="/login">Sign in</Link></p>
      <p className="rl__meta-row">Check your status? <Link to="/registration/status">Check status</Link></p>
    </>
  ) : undefined;

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      <StepIndicator step={step} />
      {error && <div className="rl__error" role="alert">{error}</div>}

      {step === 'form' && (
        <form className="rl__form" onSubmit={handleSubmit}>
          <div className="rl__field-row">
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-name">Full Name</label>
              <input id="ac-name" className="rl__input" type="text" value={displayName}
                onChange={e => setDisplayName(e.target.value)} placeholder="Your full name" required autoFocus />
            </div>
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-email">Email</label>
              <input id="ac-email" className="rl__input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
            </div>
          </div>
          <div className="rl__field-row">
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-pw">Password</label>
              <div className="rl__password-wrap">
                <input id="ac-pw" className="rl__input" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" required minLength={8} autoComplete="new-password" />
                <button type="button" className="rl__password-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-confirm">Confirm Password</label>
              <input id="ac-confirm" className="rl__input" type="password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required autoComplete="new-password" />
            </div>
          </div>
          <div className="rl__field-row">
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-academy">Academy Name</label>
              <input id="ac-academy" className="rl__input" type="text" value={academyName}
                onChange={e => setAcademyName(e.target.value)} placeholder="e.g. Greenwood Institute" required />
            </div>
            <div className="rl__field">
              <label className="rl__label" htmlFor="ac-mobile">Mobile Number</label>
              <input id="ac-mobile" className="rl__input" type="tel" value={mobile}
                onChange={e => setMobile(e.target.value)} placeholder="+91 9876543210" required autoComplete="tel" />
            </div>
          </div>
          <div className="rl__field">
            <label className="rl__label" htmlFor="ac-address">Academy Address</label>
            <textarea id="ac-address" className="rl__textarea" value={address}
              onChange={e => setAddress(e.target.value)} placeholder="Full address of your academy" required rows={2} />
          </div>
          <button type="submit" className="rl__submit"
            disabled={loading || !displayName.trim() || !email.trim() || !password || !confirmPassword || !academyName.trim() || !mobile.trim() || !address.trim()}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Submitting...</> : 'Register Academy'}
          </button>
        </form>
      )}

      {step === 'otp' && (
        <form className="rl__form" onSubmit={handleVerifyOtp}>
          <p className="rl__otp-note">{otpMessage}</p>
          <div className="rl__field">
            <label className="rl__label" htmlFor="ac-otp">Verification Code</label>
            <input id="ac-otp" className="rl__input rl__otp-input" type="text" value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="······" required maxLength={6} autoComplete="one-time-code" inputMode="numeric" autoFocus />
          </div>
          <button type="submit" className="rl__submit" disabled={loading || otpCode.length !== 6}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Verifying...</> : 'Verify Code'}
          </button>
          <div className="rl__otp-actions">
            <button type="button" className="rl__link-btn" onClick={handleResendOtp} disabled={resending}>
              {resending ? 'Sending...' : 'Resend code'}
            </button>
            <button type="button" className="rl__link-btn" onClick={() => { setStep('form'); setError(''); }}>
              Back to form
            </button>
          </div>
        </form>
      )}

      {step === 'status' && (
        <div className="rl__form">
          <div className="rl__status-icon rl__status-icon--pending">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
          <h2 className="rl__status-title">Registration Submitted</h2>
          <p className="rl__status-desc">
            {statusMessage || 'Your academy registration is now under review. This typically takes 2-3 business days.'}
          </p>
          <Link to="/login" className="rl__submit-link">Go to Login</Link>
          <div className="rl__otp-actions rl__mt-sm">
            <Link to="/registration/status" className="rl__link-btn">Check detailed status</Link>
          </div>
        </div>
      )}
    </RoleLoginShell>
  );
}
